import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { OmitStylesHTMLAttributes } from '../../types';

export const badgeVariants = cva(
    'rocco-badge inline-flex min-w-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
    {
        variants: {
            color: {
                default: 'bg-brick-800 text-primary',
                secondary: 'bg-secondary-surface text-secondary-foreground',
                destructive: 'bg-destructive text-destructive-foreground',
                outline: 'text-foreground',
                dark: 'bg-gray-800 text-primary',
                green: 'bg-[#BCE3A9] text-foreground',
                red: 'bg-red-500 text-primary',
            },
            size: {
                default: 'px-4 py-2',
                square: 'size-4.5 justify-center pr-px',
                narrow: 'px-2 py-1',
                compact: 'h-4.5 min-w-4.5 p-1',
                centeredCompact: 'h-4.5 min-w-4.5 justify-center p-1',
            },
            text: {
                default: 'text-xs font-medium',
                small: 'text-xs font-medium md:tracking-wider',
            },
        },
        defaultVariants: {
            color: 'default',
            size: 'default',
        },
    }
);

const _badgeContentVariants = cva('text-ellipsis whitespace-nowrap', {
    variants: {
        contentOverflow: {
            hidden: 'overflow-hidden',
            visible: 'overflow-visible',
        },
    },
    defaultVariants: {
        contentOverflow: 'hidden',
    },
});

interface BadgeProps
    extends OmitStylesHTMLAttributes<HTMLDivElement>,
        VariantProps<typeof badgeVariants>,
        VariantProps<typeof _badgeContentVariants> {}

export const Badge = ({
    children,
    color,
    size,
    text,
    contentOverflow,
    className,
    ...props
}: BadgeProps) => {
    return (
        <div
            className={badgeVariants({ color, size, text, className })}
            {...props}
        >
            <span
                className={_badgeContentVariants({
                    contentOverflow,
                })}
            >
                {children}
            </span>
        </div>
    );
};
